// ─────────────────────────────────────────────────────────────────────────────
// FeaturesPage, the complete catalogue of what Wavelength does.
//
// This is the deepest page on the public site, so it is built to be scanned
// rather than read: thirteen named capability groups, each with its own anchor
// so the landing page, the solutions page and the footer can deep-link straight
// into one of them. A jump list under the hero doubles as the table of contents.
//
// Four groups carry the weight of the product (cash, reconciliation, inventory,
// production) and get a full alternating <FeatureRow> with a captioned media
// panel. The rest are <FeatureGrid>s. A dark panel in the middle and a grey slab
// near the end break the list so the page never reads as one long column.
//
// There is no photography or product footage yet, so every image is a
// <MediaSlot>: a flat grey panel captioned with the asset that belongs there.
//
// Everything described here ships today. Nothing on this page names a customer,
// and nothing claims a bank feed, a payment processor other than Datacap, or any
// profitability reporting, because none of those exist.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Heading,
    Lead,
    Body,
    Key,
    Eyebrow,
    Pill,
    FloatingChip,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    StatBand,
    SpecList,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The capability groups, in page order ─────────────────────────────────────
// One entry per anchored section. The jump list, the stat count and the
// SoftwareApplication featureList all read from this array, so adding a group
// below is the only edit needed.
const GROUPS = [
    { id: 'daily-close', label: 'Daily close' },
    { id: 'cash', label: 'Cash accountability' },
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'payment-mix', label: 'Payment mix' },
    { id: 'leaks', label: 'Refunds and liabilities' },
    { id: 'sales', label: 'Sales and customers' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'receiving', label: 'Purchasing and receiving' },
    { id: 'production', label: 'Production and labels' },
    { id: 'wholesale', label: 'Wholesale' },
    { id: 'devices', label: 'Devices and apps' },
    { id: 'assistant', label: 'Assistant' },
    { id: 'platform', label: 'Platform and access' },
];

const DAILY_CLOSE = [
    { icon: 'clock', title: 'One day, one sheet', body: 'Takings, tenders, deposits and variances for a single location on a single screen. Each store closes on its own clock.' },
    { icon: 'alert', title: 'A reason, in writing', body: 'A short drawer cannot be closed with a shrug. The person closing types the reason and it stays attached to the day.' },
    { icon: 'shield', title: 'Owner-only approval', body: 'The sign-offs that touch cash sit with the owner rather than with whoever happens to be on shift.' },
    { icon: 'lock', title: 'The day locks', body: 'Once the close is signed, the day is locked. Anything altered afterwards is a recorded change, not a quiet edit.' },
    { icon: 'layers', title: 'Stores roll up', body: 'Close per location and read the group total. Nobody stitches the week together in a spreadsheet on Sunday night.' },
    { icon: 'receipt', title: 'Ready for the bookkeeper', body: 'The closed day exports as a QuickBooks journal entry CSV, in the shape your bookkeeper already imports.' },
];

const PAYMENT_MIX = [
    { icon: 'wallet', title: 'Every tender in one mix', body: 'Cash, card, EBT and SNAP, gift certificates and online payments, shown as a share of the day rather than a footnote.' },
    { icon: 'chart', title: 'Card brand split', body: 'Visa, Mastercard, Amex and Discover separated out and trended, so a shift in what customers reach for is visible early.' },
    { icon: 'bank', title: 'Settlement batches', body: 'Datacap transactions grouped into the batches that settle, each one traceable back to the store and the trading day.' },
    { icon: 'cash', title: 'Actual deposits ledger', body: 'What was banked, when, and by whom, kept as its own ledger instead of being inferred from a total.' },
    { icon: 'layers', title: 'Split tender survives', body: 'An order paid half cash and half card stays split all the way through the order record and the export.' },
    { icon: 'cart', title: 'Counter and online together', body: 'Website orders and counter sales share one set of tenders, one set of statuses and one reconciliation.' },
];

const LEAKS = [
    { icon: 'minus', title: 'Refunds need a reason', body: 'Every reversal carries a written reason and the name of the person who issued it. No blank lines in the ledger.' },
    { icon: 'shield', title: 'Thresholds that hold', body: 'Above the limit you set, a refund waits for an owner. Below it, the floor keeps moving.' },
    { icon: 'receipt', title: 'Gift certificate liability', body: 'Outstanding balances carried as a ledger, so what you owe in paper is a figure rather than a guess.' },
    { icon: 'tag', title: 'Coupons you build', body: 'Build the offer, then read what it was actually redeemed against instead of trusting the promotion to pay for itself.' },
    { icon: 'lock', title: 'EBT and SNAP apart', body: 'Benefit tenders kept on their own ledger, collected against settled, never blended into the card line.' },
    { icon: 'alert', title: 'Exceptions queue', body: 'Everything that did not tie out in one worklist, with the location, the source, the date and the amount already filled in.' },
];

const SALES = [
    { icon: 'chart', title: 'Revenue and trend', body: 'Sales, orders and average order value across any date range, filtered by location and channel.' },
    { icon: 'layers', title: 'Cut it any way', body: 'By channel, location, product, hour of the day and staff member, on the same underlying orders.' },
    { icon: 'users', title: 'Customers over time', body: 'Repeat rate, order frequency and lifetime value, so a busy week and a growing business stop being confused.' },
    { icon: 'bolt', title: 'Campaign attribution', body: 'Analytics traffic merged with the orders it actually produced, ranked by the revenue that followed.' },
    { icon: 'pin', title: 'Where orders go', body: 'Shipping and local delivery mapped, with the spread of order value across the places you serve.' },
    { icon: 'clock', title: 'Holiday and seasonal lift', body: 'A trading day compared against the same weekday in prior years, so a spike is measured rather than remembered.' },
    { icon: 'search', title: 'Every order, every line', body: 'A searchable order database down to the line item, with weighed lines carrying weight and price per pound.' },
    { icon: 'sparkle', title: 'Export from any table', body: 'CSV, Excel or a branded PDF from the page you are looking at, with the filters you applied still on it.' },
];

const RECEIVING = [
    { icon: 'boxes', title: 'Reorder board', body: 'Everything under its reorder point or PAR in one list, filtered by location and vendor.' },
    { icon: 'plus', title: 'Board to purchase order', body: 'The board prefills the purchase order, so the thing you noticed and the thing you ordered are the same thing.' },
    { icon: 'users', title: 'Vendors', body: 'Vendor records with the products they supply, their pricing and the orders raised against them.' },
    { icon: 'truck', title: 'Receiving on an iPad', body: 'The delivery is checked in at the back door on the dock, not typed up from a paper docket hours later.' },
    { icon: 'alert', title: 'Cold chain checked', body: 'A temperature check against a 41°F limit is part of receiving, recorded with the delivery rather than on a clipboard.' },
    { icon: 'check', title: 'Signed report', body: 'Ordered against received, the variance, the temperatures and the signature, shown on the purchase order in the office.' },
];

const WHOLESALE = [
    { icon: 'receipt', title: 'Invoices raise themselves', body: 'A wholesale order placed on the storefront becomes an invoice without anyone retyping it.' },
    { icon: 'scale', title: 'Ten price levels', body: 'Each level is a percentage off retail, so a catalogue price change carries through every account at once.' },
    { icon: 'users', title: 'Per-customer overrides', body: 'One account with its own price on one product, without a second catalogue to maintain.' },
    { icon: 'wallet', title: 'Partial payments', body: 'Pay some now and the rest later. The balance owed is on the invoice, not carried in anyone head.' },
    { icon: 'minus', title: 'Credit memos, not deletions', body: 'An invoice is credited on its own ledger and never removed, so the history stays readable a year later.' },
    { icon: 'bank', title: 'Aging and PDFs', body: 'Receivables bucketed by age, and a branded invoice PDF ready to send from the row you are looking at.' },
];

const DEVICE_CONTROL = [
    { icon: 'phone', title: 'Fleet health', body: 'Every register, scale and iPad in one list with its version, its last check-in and what it has been reporting.' },
    { icon: 'bolt', title: 'Configured from the office', body: 'Settings and label formats are set centrally and pushed out, rather than tapped into each device by hand.' },
    { icon: 'alert', title: 'Notifications to the floor', body: 'Orders and packing alerts pushed to staff devices and marked read when somebody has actually handled them.' },
    { icon: 'lock', title: 'Order locks and notes', body: 'Two people cannot pack the same order twice, and a note left on an order is visible to whoever picks it up next.' },
    { icon: 'pin', title: 'Live delivery map', body: 'Driver location on the map while a local delivery run is out, so the counter can answer the phone call.' },
    { icon: 'users', title: 'Staff, PINs and access', body: 'Per-app access and store assignment for every person, plus single-use temporary codes that die on first use.' },
];

const ASSISTANT = [
    { icon: 'search', title: 'Ask your own numbers', body: 'Questions answered from your workspace with the figure computed rather than guessed, and the place it came from named.' },
    { icon: 'cart', title: 'An email becomes an order', body: 'Paste a customer order and the agent matches the products, prices the lines and hands back a draft to check.' },
    { icon: 'receipt', title: 'It reads what you send it', body: 'A photograph of a handwritten order list or an attached document is read as text before the matching starts.' },
    { icon: 'book', title: 'It knows the workspace', body: 'Answers point at the page that holds the detail, so the next click is the report rather than another question.' },
];

const PLATFORM = [
    { icon: 'shield', title: 'Roles across 20 modules', body: 'Eight presets to start from, then per-module control, so a store manager sees their store and nothing else.' },
    { icon: 'users', title: 'Collaborators and view-as', body: 'Invite a bookkeeper or a partner, and see the workspace exactly as one of your own roles sees it.' },
    { icon: 'layers', title: 'Workspaces stay apart', body: 'Run more than one business from one login. Switching stores clears the screen rather than mixing two sets of numbers.' },
    { icon: 'lock', title: 'Append-only audit log', body: 'Who changed what, when and from where. Manual changes are added to the record, never written over it.' },
    { icon: 'check', title: 'Source data is never rewritten', body: 'Adjustments land on an overrides layer above the raw records, so the original numbers survive every correction.' },
    { icon: 'sparkle', title: 'Branded exports everywhere', body: 'CSV, Excel and PDF from every table and dashboard, carrying your name rather than a spreadsheet default.' },
];

const APPS = [
    { label: 'Register', value: 'Takes the order, the weight and the payment' },
    { label: 'Scale', value: 'Weighs, prices and prints your label format' },
    { label: 'Packing', value: 'Pushes the order to whoever is packing it' },
    { label: 'Receiving', value: 'Signs off the delivery at the back door' },
    { label: 'Delivery', value: 'Driver location while the run is out' },
];

const SOURCES = [
    { label: 'Registers and online store', value: 'Works alongside what you run; WooCommerce syncs read only' },
    { label: 'Card processing', value: 'Datacap transactions and settlement batches' },
    { label: 'Bank', value: 'Statement CSV import, duplicates rejected' },
    { label: 'Bookkeeping', value: 'QuickBooks journal entry CSV' },
    { label: 'Marketing', value: 'Google Analytics 4 campaign traffic' },
    { label: 'Sign-in and hosting', value: 'Amazon Cognito, on Amazon Web Services' },
    { label: 'Anything else', value: 'New sources added on request' },
];

// ── Page ─────────────────────────────────────────────────────────────────────
export default function FeaturesPage() {
    return (
        <MarketingShell active="features">
            <Seo
                title="Everything in the platform, feature by feature"
                description="Every part of the Wavelength back office platform: daily close, cash control, three-way reconciliation, sales, inventory, receiving, production and wholesale."
                path="/features"
                keywords="retail back office software, daily close software, cash accountability software, three way reconciliation, bank statement import, payment mix reporting, multi location inventory ledger, purchase order and receiving app, production label reporting, wholesale invoicing software, role based access retail"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'SoftwareApplication',
                            '@id': 'https://wavelengtherp.com/features#software',
                            name: 'Wavelength',
                            applicationCategory: 'BusinessApplication',
                            applicationSubCategory: 'Retail back office and reconciliation',
                            operatingSystem: 'Web, iOS, iPadOS',
                            url: 'https://wavelengtherp.com/features',
                            description:
                                'Back-office platform for multi-location retail and food operators: daily close, cash accountability, three-way reconciliation against processor batches and imported bank statements, sales analytics, inventory, receiving, production, wholesale invoicing and device control.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                            featureList: GROUPS.map((g) => g.label),
                            // Must agree with the published plans on /pricing
                            // (Starter 149, Growth 399). Structured data that
                            // quotes a price the site never shows gets flagged.
                            offers: {
                                '@type': 'AggregateOffer',
                                priceCurrency: 'USD',
                                lowPrice: '149',
                                highPrice: '399',
                                offerCount: '2',
                                category: 'SaaS subscription',
                                url: 'https://wavelengtherp.com/pricing',
                            },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Platform', item: 'https://wavelengtherp.com/features' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero, with the group list doubling as a table of contents ─── */}
            <PageHero
                eyebrow="Everything in the platform"
                title="Everything Wavelength does."
                lead={<>The whole catalogue, in the order a trading day happens: close, cash, reconciliation, tenders, leaks, sales, stock, receiving, production, wholesale, devices and access. <Key>Keep your point of sale.</Key> This is the back office around it.</>}
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'See pricing', to: '/pricing' }}
            >
                <div className="flex flex-wrap gap-2">
                    {GROUPS.map((g) => (
                        <Pill key={g.id} as="a" href={`#${g.id}`}>{g.label}</Pill>
                    ))}
                </div>
            </PageHero>

            {/* ── The shape of it ────────────────────────────────────────────── */}
            <Section tone="paper2" pad="md">
                <Container>
                    <Reveal>
                        <StatBand
                            countUp
                            stats={[
                                { value: String(GROUPS.length), label: 'Capability groups', note: 'All included, on every plan' },
                                { value: '5', label: 'Apps on the floor', note: 'Register, scale, packing, receiving, delivery' },
                                { value: '3', label: 'Sources reconciled', note: 'Register takings, processor batches, bank' },
                                { value: '20', label: 'Modules under role control', note: 'Eight presets to start from' },
                            ]}
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 1. Daily close ─────────────────────────────────────────────── */}
            <Section id="daily-close" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="01 · Daily close"
                            title="The day closes where it happened."
                            lead="One close per location per day, done by the person who was there, signed off before anyone goes home. Everything after this section reads off that one honest record."
                            actions={<Link to="/how-it-works" className={BTN_SECONDARY}>How a day runs<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={DAILY_CLOSE} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 2. Cash accountability ─────────────────────────────────────── */}
            <Section id="cash" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="02 · Cash accountability"
                            title="Count blind. Sign your name."
                            body="A drawer that shows the expected total is not a count, it is a confirmation. Wavelength hides the figure until the money is counted, then records who counted it, on which drawer, at what time."
                            bullets={[
                                'Blind drawer and safe counts, with the expected total withheld until the entry is finished',
                                'Over and short attributed by person, by shift and by store, and trended rather than absorbed',
                                'Deposits kept as their own ledger and matched against what actually reached the bank',
                                'Drawer, drop, safe, deposit and bank: one chain of custody for every dollar',
                            ]}
                            cta={<Link to="/solutions" className={BTN_SECONDARY}>Who runs it this way<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Blind count screen mid entry"
                                    note="iPad held at the safe, denominations being keyed, the expected total nowhere on the screen"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 3. Reconciliation, the big breath ──────────────────────────── */}
            <Section id="reconciliation" pad="xl">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="03 · Reconciliation"
                            title="Three sources. One answer."
                            body="What the registers rang, what the processor settled and what the bank received are three different numbers until something makes them agree. Wavelength matches all three and hands you only the difference."
                            bullets={[
                                'Bank activity imported from a statement file, with duplicate rows rejected on the way in',
                                'Datacap transactions and settlement batches traced back to the store and the trading day',
                                'Money still unconfirmed is aged, so nothing sits quietly for a month waiting to be noticed',
                                'Anything unmatched lands in the exceptions queue with its location, source, date and amount',
                            ]}
                            cta={<Link to="/integrations" className={BTN_SECONDARY}>What it reads<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Reconciliation screen, three columns matching"
                                    note="Register takings, processor batches and bank deposits side by side, the unmatched rows pulled out"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-10 flex flex-wrap items-center gap-2">
                            <Pill active>Statement import</Pill>
                            <Pill>Duplicate rejection</Pill>
                            <Pill>Processor batches</Pill>
                            <Pill>Deposit matching</Pill>
                            <Pill>Aged unconfirmed</Pill>
                            <Pill>Exceptions queue</Pill>
                            <Pill>Audit log</Pill>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── 4. Payment mix ─────────────────────────────────────────────── */}
            <Section id="payment-mix" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="04 · Payment mix"
                            title="Every tender, followed to the payout."
                            lead="Cash is only one way the money arrives. Each tender is counted where it was taken, then followed through the batch that settled it and the deposit that landed."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={PAYMENT_MIX} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Dark panel: chain of custody ───────────────────────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px] lg:gap-14">
                                <div className="flex flex-col">
                                    <Eyebrow className="!text-white/45">The idea underneath all of it</Eyebrow>
                                    <h2 className="mt-4 max-w-[460px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        Every dollar has a chain of custody.
                                    </h2>
                                    <div className="mt-7 flex flex-wrap gap-2">
                                        {['Rung', 'Drawer', 'Drop', 'Safe', 'Deposit', 'Bank', 'Matched'].map((c, i) => (
                                            <Pill key={c} dark active={i === 0}>{c}</Pill>
                                        ))}
                                    </div>
                                    <div className="mt-auto max-w-[460px] pt-12">
                                        <h3 className="text-[19px] font-semibold tracking-[-0.015em] text-white sm:text-[22px]">
                                            Nothing is silently overwritten
                                        </h3>
                                        <p className="mt-2.5 text-[14px] leading-[1.65] text-white/55">
                                            Stock levels, corrections and closed days are recorded as new entries rather than edits to old ones. When a number changes, the previous number and the person who changed it are both still there to read.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Diagram"
                                        label="The custody chain drawn end to end"
                                        note="A single sale followed from the register through the safe and the deposit to the matched bank line"
                                        ratio="3 / 4"
                                        className="!rounded-[28px] max-w-[340px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── 5. Refunds, certificates, coupons, benefit tenders ─────────── */}
            <Section id="leaks" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="05 · Refunds and liabilities"
                            title="The quiet ways money leaves."
                            lead="Shrinkage rarely announces itself. It shows up as a refund with no reason, a certificate nobody tracked, a promotion nobody measured and a benefit tender folded into the card line."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={LEAKS} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 6. Sales and customers ─────────────────────────────────────── */}
            <Section id="sales" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="06 · Sales and customers"
                            title="What sold, where, and to whom."
                            lead="Counter sales and online orders in the same tables, with the same statuses and the same tenders, so the answer to any question is one number instead of two that disagree."
                            actions={<Link to="/solutions" className={BTN_SECONDARY}>By business type<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={SALES} cols={4} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 7. Inventory ───────────────────────────────────────────────── */}
            <Section id="inventory" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="07 · Inventory"
                            title="On hand, because it moved."
                            body="Stock is not a number somebody typed. It is the sum of everything received, produced, sold, wasted and counted, kept as an append-only ledger you can read back line by line."
                            bullets={[
                                'Every movement dated and sourced, and reversible only by another movement',
                                'On hand derived from the ledger and never stored, so it cannot drift away from its own history',
                                'Reorder points and PAR per product and per location, with storage locations on the record',
                                'Product catalogue with price history, shelf labels, barcode formats and raw materials',
                            ]}
                            cta={<Link to="/how-it-works" className={BTN_SECONDARY}>How stock is tracked<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Product page with its movement history"
                                    note="A single product, on hand at the top, every receipt, sale and adjustment listed beneath it"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 8. Purchasing and receiving ────────────────────────────────── */}
            <Section id="receiving" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="08 · Purchasing and receiving"
                            title="From the reorder board to the back door."
                            lead="The gap between noticing that something is low and proving that it arrived is where most stock problems live. Both ends are in the same system here."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={RECEIVING} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 9. Production and labels ───────────────────────────────────── */}
            <Section id="production" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="09 · Production and labels"
                            title="Every label is a production record."
                            body="The scale on the bench is where product is actually made. Each label it prints is recorded as production, so the counter, the office and the stock ledger all learn about the same pack at the same moment."
                            bullets={[
                                'Volume, weight and value rolled up by product, by scale and by staff member',
                                'Label formats designed in the office and pushed out to every scale in your own running order',
                                'Barcode formats shaped to your labels rather than to a default somebody else chose',
                                'Yield and waste followed from raw material through to finished pack',
                            ]}
                            cta={<Link to="/solutions" className={BTN_SECONDARY}>Built for production counters<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="A scale printing a label on the production bench"
                                    note="Close in, hands and a tray, the printed label coming off the machine"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 10. Wholesale ──────────────────────────────────────────────── */}
            <Section id="wholesale" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="10 · Wholesale"
                            title="Trade accounts, billed without retyping."
                            lead="The accounts that buy by the case need pricing, invoices, part payments and a credit note that does not destroy the history. That is a second business, and it runs in the same workspace."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={WHOLESALE} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 11. Devices and apps, on the grey slab ─────────────────────── */}
            <Section id="devices" pad="lg">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#F0F0F0] via-[#F7F7F7] to-[#E9E9E9] p-6 sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                                <div className="flex flex-col gap-5">
                                    <Eyebrow>11 · Devices and apps</Eyebrow>
                                    <Heading className="max-w-[420px]">Five apps on the floor.</Heading>
                                    <Lead className="max-w-[420px]">
                                        Each one feeds the same workspace, so what happens at the counter, the bench, the packing table, the back door and the van is already in the numbers before anyone opens a laptop.
                                    </Lead>
                                    <SpecList rows={APPS} className="mt-1" />
                                    <div className="mt-1">
                                        <Link to="/integrations" className={BTN_PRIMARY}>What it connects to<Icon name="arrow" className="h-4 w-4" /></Link>
                                    </div>
                                </div>
                                <div className="relative">
                                    <MediaSlot
                                        kind="Photo"
                                        label="The device family on a counter"
                                        note="Register, scale and iPad together, shot from above on a neutral surface"
                                        ratio="1 / 1"
                                        className="!rounded-[28px] !bg-[#E4E4E4]"
                                    />
                                    <div className="pointer-events-none absolute inset-0 hidden sm:block">
                                        <FloatingChip className="absolute left-4 top-6">Works offline</FloatingChip>
                                        <FloatingChip className="absolute right-4 top-1/3">Configured centrally</FloatingChip>
                                        <FloatingChip className="absolute bottom-8 left-8">Health reported</FloatingChip>
                                        <FloatingChip className="absolute bottom-20 right-6">Included in the plan</FloatingChip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-4" items={DEVICE_CONTROL} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── 12. Assistant ──────────────────────────────────────────────── */}
            <Section id="assistant" tone="paper2" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Eyebrow>12 · Assistant</Eyebrow>
                                <Heading>Ask the workspace directly.</Heading>
                                <Lead>
                                    A question typed in plain language, answered from your own records with the arithmetic done rather than approximated.
                                </Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_SECONDARY}>See it on your data<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <FeatureGrid items={ASSISTANT} cols={2} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── 13. Platform, access and sources ───────────────────────────── */}
            <Section id="platform" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="13 · Platform and access"
                            title="Who sees what, and where it all sits."
                            lead="Several locations means several people with different reasons to be in the system. Access is scoped per module, changes are logged, and the raw records underneath are never rewritten."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={PLATFORM} cols={3} />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[380px_1fr] lg:gap-16">
                            <div className="flex flex-col gap-4">
                                <Heading>What it reads and writes.</Heading>
                                <Body>
                                    Wavelength sits on top of the systems you already run. Bank activity arrives as an imported statement rather than a live feed, and bookkeeping leaves as a file your accountant imports.
                                </Body>
                                <div className="mt-1">
                                    <Link to="/integrations" className={BTN_SECONDARY}>All integrations<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                            <SpecList rows={SOURCES} />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="Read the list, then close one day."
                body="Every group above is included on every plan. Start with one location and one trading day, and see which of them earns its place first."
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Book a walkthrough', to: '/contact' }}
            />
        </MarketingShell>
    );
}
